<template>
  <SubNavigation
    :title="pageData.name"
    :children-links="pageData.parentNodePage?.children"
  />

  <Selector
    :placeholder="t('common.all_announcements')"
    v-model="selectedOption"
    :options="announcementTags"
    bg-color="bg-[#E4E4E4]"
    class="bp1680:w-[506px]! mx-auto w-[275px]! sm:w-[366px]! md:w-[354px]! lg:w-[452px]! xl:w-[426px]! 2xl:w-[506px]!"
  />

  <Container
    class="bp1680:w-[860px] mx-auto min-h-50 w-full px-5 transition-opacity duration-300 md:w-[479px] md:px-0 lg:w-[616px] lg:px-0! xl:w-[740px] xl:px-0! 2xl:w-[860px]"
    :class="{ 'pointer-events-none opacity-40': pending }"
  >
    <NoticeList
      v-if="noticesAccumulated?.length"
      :title="t('common.announcements')"
      :notices="noticesAccumulated"
      disable-collapse
    />

    <section v-else-if="!pending" class="py-12 md:py-20">
      <p class="text-center text-2xl md:text-4xl xl:text-6xl">
        {{ t('common.no_announcements_found') }}
      </p>
    </section>

    <ShowMoreButton
      v-if="hasMorePages"
      :is-expanded="false"
      type="border"
      class="mt-7.5 xl:mt-10"
      button-color="dark"
      @click="loadMore"
    />
  </Container>

  <div
    v-if="hasData"
    class="mt-20 mb-30 w-full px-5 md:mx-auto md:mt-25 md:mb-37.5 md:w-151.25 md:px-0 lg:w-195 xl:mt-30 xl:mb-42.5 xl:w-223.25! 2xl:mt-35! 2xl:mb-47.5! 2xl:w-263.5!"
  >
    <Archives
      :years="years"
      :archive-data="archiveStore"
      :has-more-years="hasMoreYears"
      :is-notice="true"
      @load-more="(year) => loadMoreForYear(year)"
      @load-more-years="loadMoreYears"
    />
  </div>
</template>

<script setup lang="ts">
import type { Notice } from '~/types'

interface Props {
  pageData: any
}

const props = defineProps<Props>()

const PER_PAGE = 3
const { throwFetchError, throwNotFound } = useThrowErrors()
const { locale, t } = useI18n()

const route = useRoute()
const router = useRouter()
const windowWidth = ref(0)

useDynamicSeo(props.pageData, {
  bodyAttrs: {
    style: 'background-color: #E4E4E4 ',
  },
})

const initialQuery = (route.query.category as string) || ''
const initialFilterSlug =
  initialQuery && initialQuery !== 'vse' && initialQuery !== 'all'
    ? initialQuery
    : undefined

const filterSlug = ref<string | undefined>(initialFilterSlug)

const tagSlugsComputed = computed<string[] | undefined>(() => {
  const slug = filterSlug.value
  if (!slug) return undefined
  return [slug]
})

const currentPage = ref(1)

const pageVariables = reactive({
  first: 5,
  page: currentPage,
  tagSlugs: tagSlugsComputed,
})

const [
  { data: announcementTagsData },
  { data: noticesData, pending, error: noticesError },
  archiveRes,
] = await Promise.all([
  usePersistedFetch('AnnouncementTags'),
  usePersistedFetch('GetAnnouncementsPaginated', pageVariables, {
    watch: [currentPage, tagSlugsComputed],
  }),
  useArchive<Notice>('Oznameni', PER_PAGE, filterSlug),
])

const {
  years,
  archiveStore,
  loadMoreForYear,
  loadMoreYears,
  hasMoreYears,
  hasData,
} = archiveRes

if (noticesError.value) throwFetchError()
if (!noticesData.value?.getAnnouncementsPaginated) throwNotFound()

const announcementTags = computed(() => {
  return [
    {
      name: t('common.all_announcements'),
      slug: locale.value === 'cs' ? 'vse' : 'all',
    },
    ...(announcementTagsData.value?.getAnnouncementTags?.map((t: any) => ({
      name: t.name,
      slug: t.slug[locale.value],
    })) || []),
  ]
})

const selectedOption = ref(
  announcementTags.value.find((opt) => opt.slug === initialQuery) ||
    announcementTags.value[0],
)

const appliedOption = ref(selectedOption.value)

const noticesAccumulated = ref<Notice[]>([])
const hasMorePages = ref(false)

watch(
  () => noticesData.value,
  (newVal) => {
    if (!newVal?.getAnnouncementsPaginated) return
    const paginated = newVal.getAnnouncementsPaginated
    const newData = paginated.data || []
    hasMorePages.value = paginated.paginatorInfo?.hasMorePages || false

    if (currentPage.value === 1) {
      noticesAccumulated.value = [...newData]
    } else {
      noticesAccumulated.value.push(...newData)
    }
  },
  { immediate: true },
)

const loadMore = () => {
  if (hasMorePages.value) {
    currentPage.value++
  }
}

onMounted(() => {
  if (!process.client) return
  windowWidth.value = window.innerWidth
  window.addEventListener('resize', () => {
    windowWidth.value = window.innerWidth
  })
})

onUnmounted(() => {
  if (!process.client) return
  window.removeEventListener('resize', () => {
    windowWidth.value = window.innerWidth
  })
})

let filterTimer: ReturnType<typeof setTimeout> | null = null

watch(selectedOption, (newValue) => {
  if (filterTimer) clearTimeout(filterTimer)

  filterTimer = setTimeout(() => {
    currentPage.value = 1
    appliedOption.value = newValue

    const slug = newValue?.slug
    filterSlug.value =
      !slug || slug === 'vse' || slug === 'all' ? undefined : slug

    router.push({
      query: {
        ...route.query,
        category: newValue ? newValue.slug : undefined,
      },
    })
  }, 300)
})
</script>
